import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { bonVisiteAPI } from '../../lib/api';
import { HomlyLogo } from '../../components/brand/HomlyLogo';
import {
    FileSignature, CheckCircle, PenTool, XCircle, Loader2, Shield, Check, Home, Calendar
} from 'lucide-react';

const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return iso; }
};

export default function BonVisiteSignPage() {
    const { token } = useParams();
    const [slip, setSlip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [signed, setSigned] = useState(false);
    const [signing, setSigning] = useState(false);
    const [accept, setAccept] = useState(false);
    const [fullName, setFullName] = useState('');

    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const [hasSignature, setHasSignature] = useState(false);

    useEffect(() => {
        bonVisiteAPI.getForSigning(token).then(r => {
            setSlip(r.data);
            if (r.data.status === 'signed') setSigned(true);
            const b = r.data.buyers?.[0];
            if (b) setFullName(`${b.first_name} ${b.last_name}`);
        }).catch(() => setError("Ce bon de visite est introuvable ou a expiré."))
            .finally(() => setLoading(false));
    }, [token]);

    const pos = (e) => {
        const c = canvasRef.current;
        const r = c.getBoundingClientRect();
        const cx = e.clientX ?? e.touches?.[0]?.clientX;
        const cy = e.clientY ?? e.touches?.[0]?.clientY;
        return { x: (cx - r.left) * (c.width / r.width), y: (cy - r.top) * (c.height / r.height) };
    };
    const start = (e) => {
        e.preventDefault();
        drawing.current = true;
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = pos(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };
    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = pos(e);
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#111827';
        ctx.lineTo(x, y);
        ctx.stroke();
        setHasSignature(true);
    };
    const end = () => { drawing.current = false; };
    const clear = () => {
        const c = canvasRef.current;
        c.getContext('2d').clearRect(0, 0, c.width, c.height);
        setHasSignature(false);
    };

    const submit = async () => {
        if (!hasSignature || !accept || !fullName.trim()) return;
        setSigning(true);
        try {
            const dataUrl = canvasRef.current.toDataURL('image/png');
            await bonVisiteAPI.sign(token, { signature_data: dataUrl, signed_full_name: fullName.trim(), accept: true });
            setSigned(true);
        } catch (e) {
            setError(e?.response?.data?.detail || "Erreur lors de la signature.");
        } finally {
            setSigning(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0f0a1a] to-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF914D]" /></div>;
    }
    if (error && !slip) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0f0a1a] to-slate-950 flex items-center justify-center p-4">
                <div className="text-center"><XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" /><h1 className="text-xl font-bold text-white mb-2">Lien invalide</h1><p className="text-white/50">{error}</p></div>
            </div>
        );
    }
    if (signed) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0f0a1a] to-slate-950 flex items-center justify-center p-4">
                <div className="text-center max-w-sm" data-testid="slip-signed-success">
                    <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-green-400" /></div>
                    <h1 className="text-2xl font-bold text-white mb-3">Bon de visite signé</h1>
                    <p className="text-white/60 mb-6">Merci. Votre signature a bien été enregistrée et transmise à l&apos;agence.</p>
                    <HomlyLogo size="sm" />
                </div>
            </div>
        );
    }

    const prop = slip.property_ref || {};

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0f0a1a] to-slate-950" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
            <div className="max-w-md mx-auto px-4 py-6 space-y-5">
                <div className="text-center"><HomlyLogo size="sm" /></div>

                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <FileSignature className="w-5 h-5 text-[#FF914D]" />
                        <h1 className="text-lg font-bold text-white">Bon de visite</h1>
                    </div>
                    <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-white/70"><Home className="w-4 h-4 text-white/40" /> {prop.type} · Réf. {prop.reference}{prop.city ? ` · ${prop.city}` : ''}</div>
                        <div className="flex items-center gap-2 text-white/70"><Calendar className="w-4 h-4 text-white/40" /> Visite du {fmtDate(slip.visit_date)}</div>
                    </div>
                    <p className="text-[11px] text-white/40 mt-3">
                        Présenté par <strong className="text-white/70">{slip.agency_name}</strong>{slip.agent_name ? ` · ${slip.agent_name}` : ''}.
                        {' Adresse exacte et identité du vendeur communiquées par l\u2019agence.'}
                    </p>
                </div>

                <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-xs text-white/60 leading-relaxed" data-testid="slip-legal">
                    {`Je reconnais avoir visité le bien référencé ci-dessus, présenté par l’agence ${slip.agency_name}. Je reconnais que c’est par son intermédiaire que j’en ai eu connaissance et m’engage à ne pas traiter directement avec le propriétaire, sous peine des honoraires prévus au mandat. Ce bon ne constitue pas une offre d’achat.`}
                </div>

                <div>
                    <label className="text-xs text-white/50 mb-1 block">Nom complet du signataire *</label>
                    <input value={fullName} onChange={e => setFullName(e.target.value)}
                        className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#681793]/50"
                        data-testid="slip-fullname" />
                </div>

                <div>
                    <div className="flex items-center gap-2 mb-2"><PenTool className="w-4 h-4 text-[#FF914D]" /><span className="text-sm font-semibold text-white">Votre signature</span></div>
                    <div className="bg-white rounded-2xl p-3">
                        <canvas ref={canvasRef} width={500} height={180}
                            className="w-full border-2 border-dashed border-gray-300 rounded-xl touch-none cursor-crosshair"
                            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                            data-testid="slip-canvas" />
                    </div>
                    <button onClick={clear} className="text-xs text-white/50 hover:text-white underline mt-2" data-testid="slip-clear">Effacer</button>
                </div>

                <label className="flex items-start gap-3 cursor-pointer" onClick={() => setAccept(!accept)} data-testid="slip-accept">
                    <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center mt-0.5 transition-colors ${accept ? 'bg-[#681793] border-[#681793]' : 'border-white/30'}`}>
                        {accept && <Check className="w-4 h-4 text-white" />}
                    </div>
                    <span className="text-white/70 text-xs">Je certifie avoir lu et approuvé le présent bon de visite. Ma signature électronique a la même valeur qu&apos;une signature manuscrite (règlement eIDAS).</span>
                </label>

                {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm">{error}</div>}

                <div className="flex items-center justify-center gap-2 text-white/40 text-xs"><Shield className="w-4 h-4" /> Signature électronique sécurisée</div>

                <button onClick={submit} disabled={!hasSignature || !accept || !fullName.trim() || signing}
                    data-testid="slip-sign-btn"
                    className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all ${hasSignature && accept && fullName.trim() && !signing ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white' : 'bg-white/10 text-white/40 cursor-not-allowed'}`}>
                    {signing ? <><Loader2 className="w-5 h-5 animate-spin" /> Signature…</> : <><CheckCircle className="w-5 h-5" /> Signer le bon de visite</>}
                </button>
            </div>
        </div>
    );
}
